import { type TextBox as TextBoxType, TextBoxStates } from "client/store/features/memeGenerator/types";
import { DragHandler } from "./DragHandler/DragHandler";
import { ResizeHandlers } from "./ResizeHandlers/ResizeHandlers";
import { RotateHandler } from "./RotateHandler/RotateHandler";
import { useTextBox } from "./useTextBox";

export interface TextBoxProps {
    textBox: TextBoxType;
}

const invisibleDragAreaPadding = 20;

export const TextBox = ({ textBox }: TextBoxProps) => {
    const {
        dragEventHandlers,
        onBlurOverlayClick,
        onFocus,
        parentContainerRef,
        contentEditableRef,
        onContentEditableClick,
        onParentClick,
        handleInput,
        onContentEditablePointerDown,
    } = useTextBox(textBox);
    return (
        <>
            <div
                className={`mg-textBox-border absolute touch-none left-0 top-0 cursor-move z-20`}
                style={{
                    width: textBox.width,
                    height: textBox.height,
                    transform: `translate(${textBox.left}px,${textBox.top}px) rotate(${textBox.rotation}deg)`,
                    borderColor: textBox.showBorder ? undefined : "transparent",
                    background: textBox.showBorder || textBox.state !== TextBoxStates.BLUR ? undefined : "none",
                    zIndex: 10 + textBox.zIndex,
                }}
                ref={parentContainerRef}
                {...dragEventHandlers}
                onClick={onParentClick}
            >
                <div
                    className={`w-full h-full relative ${
                        textBox.state === TextBoxStates.INSERT ? "cursor-text" : "cursor-move"
                    } `}
                >
                    <>
                        <ResizeHandlers
                            textBox={textBox}
                            contentEditableRef={contentEditableRef}
                            textBoxContainerRef={parentContainerRef}
                        />
                        <div className="absolute left-0 right-0 flex items-center justify-center gap-4 mt-4 pointer-events-none top-full">
                            <RotateHandler textBox={textBox} />
                            <DragHandler textBox={textBox} />
                        </div>
                    </>
                    <div
                        className={`w-full h-full flex ${
                            textBox.alignment.horizontal === "start"
                                ? "text-left justify-start"
                                : textBox.alignment.horizontal === "middle"
                                ? "text-center justify-center"
                                : "text-right justify-end"
                        } ${
                            textBox.alignment.vertical === "start"
                                ? "items-start"
                                : textBox.alignment.vertical === "middle"
                                ? "items-center"
                                : "items-end"
                        } z-10`}
                    >
                        <div
                            className={`${textBox.forceCaps && "uppercase"} outline-none w-full select-none ${
                                textBox.decoration.underline ? "underline" : ""
                            } ${textBox.decoration.italic ? "italic" : ""} ${
                                textBox.decoration.bold ? "font-semibold" : ""
                            }`}
                            contentEditable={true}
                            ref={contentEditableRef}
                            defaultValue={textBox.value}
                            id={`textBox-${textBox.id}`}
                            onInput={handleInput}
                            onFocus={onFocus}
                            onClick={onContentEditableClick}
                            spellCheck={false}
                            onPaste={(e) => {
                                e.preventDefault();
                            }}
                            style={{
                                fontSize: `${textBox.fontSize}px`,
                                fontFamily: textBox.fontFamily,
                                color: textBox.fontColor,
                                letterSpacing: `${textBox.spacing.letter}px`,
                                WebkitTextStroke: "0px",
                                lineHeight: textBox.spacing.line,
                                textShadow: textBox.shadow?.isActive
                                    ? `${Array.from(Array(10).keys())
                                          .map(() => `0px 0px ${textBox.shadow?.blurRadius}px ${textBox.shadow?.color}`)
                                          .join(",")}`
                                    : undefined,
                                opacity: textBox.opacity / 100,
                            }}
                            onPointerDown={onContentEditablePointerDown}
                        ></div>
                    </div>
                    {/**
                     * blur overlay
                     */}
                    <div
                        className={`absolute inset-0 ${
                            textBox.state === TextBoxStates.BLUR ? "z-30" : "-z-30"
                        } cursor-move`}
                        onClick={onBlurOverlayClick}
                    ></div>
                </div>
            </div>
            <div
                className="absolute top-0 left-0 bg-transparent cursor-move"
                style={{
                    width: textBox.width + invisibleDragAreaPadding,
                    height: textBox.height + invisibleDragAreaPadding,
                    transform: `translate(${textBox.left - invisibleDragAreaPadding / 2}px,${
                        textBox.top - invisibleDragAreaPadding / 2
                    }px) rotate(${textBox.rotation}deg)`,
                    zIndex: 9 + textBox.zIndex,
                }}
                {...dragEventHandlers}
            ></div>
            {/**
             * underlay
             */}
            <div
                className="absolute top-0 left-0 z-0 mg-textBox-border touch-none"
                style={{
                    width: textBox.width,
                    height: textBox.height,
                    transform: `translate(${textBox.left}px,${textBox.top}px) rotate(${textBox.rotation}deg)`,
                    borderColor: "transparent",
                    background: "none",
                }}
            >
                <div className={`w-full h-full relative`}>
                    <div
                        className={`w-full h-full flex ${
                            textBox.alignment.horizontal === "start"
                                ? "text-left justify-start"
                                : textBox.alignment.horizontal === "middle"
                                ? "text-center justify-center"
                                : "text-right justify-end"
                        } ${
                            textBox.alignment.vertical === "start"
                                ? "items-start"
                                : textBox.alignment.vertical === "middle"
                                ? "items-center"
                                : "items-end"
                        } z-10`}
                    >
                        <div
                            className={`${textBox.forceCaps && "uppercase"} outline-none w-full select-none ${
                                textBox.decoration.italic ? "italic" : ""
                            } ${textBox.decoration.bold ? "font-semibold" : ""}`}
                            defaultValue={textBox.value}
                            dangerouslySetInnerHTML={{ __html: textBox.innerHTML }}
                            style={{
                                fontSize: `${textBox.fontSize}px`,
                                fontFamily: textBox.fontFamily,
                                color: textBox.fontColor,
                                letterSpacing: `${textBox.spacing.letter}px`,
                                WebkitTextStroke: textBox.outline?.isActive
                                    ? `${textBox.outline.size}px ${textBox.outline.color}`
                                    : undefined,
                                lineHeight: textBox.spacing.line,
                                opacity: textBox.opacity / 100,
                            }}
                        ></div>
                    </div>
                </div>
            </div>
        </>
    );
};
